import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, CheckCircle2, X, Key, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PREMIUM_KEY_STORAGE = "fp_premium_key";

/**
 * FullAuditLeadDialog — Lead-gen popup replacing the Full Audit purchase flow.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - product: string — auto-filled product context (e.g. "Xiaomi Redmi Note 14")
 */
export default function FullAuditLeadDialog({ open, onClose, product = "" }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Hidden admin override path
  const [mode, setMode] = useState("lead"); // 'lead' | 'code'
  const [keyInput, setKeyInput] = useState("");
  const [keyChecking, setKeyChecking] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error("Mohon masukkan email yang valid.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/full-audit`, {
        email: email.trim().toLowerCase(),
        product: product || null,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Gagal mengirim email. Coba lagi.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEmail("");
    setSubmitted(false);
    setMode("lead");
    setKeyInput("");
    onClose?.();
  };

  const handleSubmitKey = async (e) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    setKeyChecking(true);
    try {
      const r = await axios.get(`${API}/payments/key-status`, { params: { key: k }, timeout: 10000 });
      if (r.data?.valid) {
        localStorage.setItem(PREMIUM_KEY_STORAGE, k);
        toast.success("Akses Premium diaktifkan.");
        // Trigger storage event manually so other components (countdown) pick it up
        window.dispatchEvent(new StorageEvent("storage", { key: PREMIUM_KEY_STORAGE, newValue: k }));
        // Soft reload so AuditForm useEffect re-reads the key
        setTimeout(() => window.location.reload(), 600);
        handleReset();
      } else {
        toast.error("Kode tidak valid atau sudah expired.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kode tidak valid atau sudah expired.";
      toast.error(msg);
    } finally {
      setKeyChecking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={handleReset}
      data-testid="full-audit-lead-dialog"
    >
      <div
        className="relative w-full max-w-md bg-white border-2 border-black shadow-brutal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleReset}
          className="absolute top-3 right-3 border-2 border-black bg-white hover:bg-[#FF2400] hover:text-white p-1.5"
          aria-label="Tutup"
          data-testid="lead-dialog-close"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>

        {/* SUCCESS VIEW */}
        {submitted && (
          <div className="p-6 md:p-8 text-center" data-testid="lead-success-view">
            <div className="mx-auto w-14 h-14 bg-[#FFD700] border-2 border-black grid place-items-center">
              <CheckCircle2 className="w-7 h-7 text-[#0a0a0a]" strokeWidth={2.5} />
            </div>
            <h3 className="mt-4 font-display font-black uppercase tracking-tight text-2xl md:text-3xl leading-tight">
              Terima Kasih.
            </h3>
            <p className="mt-3 font-sans text-sm md:text-base leading-relaxed text-[#333]">
              Kami akan menghubungi Anda saat akses <span className="font-bold">Premium</span> tersedia.
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-5 border-2 border-black bg-[#0a0a0a] text-[#FFD700] px-5 py-2.5 font-mono font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#FFD700] hover:text-black transition-colors"
              data-testid="lead-close-button"
            >
              Tutup
            </button>
          </div>
        )}

        {/* CODE INPUT VIEW (admin override) */}
        {!submitted && mode === "code" && (
          <form onSubmit={handleSubmitKey} className="p-6 md:p-7" data-testid="lead-code-form">
            <button
              type="button"
              onClick={() => setMode("lead")}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#666] hover:text-black inline-flex items-center gap-1"
              data-testid="lead-code-back"
            >
              <ArrowLeft className="w-3 h-3" /> Kembali
            </button>
            <h3 className="mt-3 font-display font-black uppercase tracking-tighter text-2xl md:text-3xl leading-[0.95]">
              Masukkan Kode Akses
            </h3>
            <p className="mt-2 text-sm font-sans leading-relaxed text-[#333]">
              Kode akses internal untuk admin/owner. Jika belum punya, gunakan form lead di sebelumnya.
            </p>
            <label className="mt-5 block font-mono text-[10px] uppercase tracking-[0.22em] text-[#666]">
              Kode Akses
            </label>
            <div className="mt-1 flex items-center border-2 border-black bg-white">
              <Key className="w-4 h-4 ml-3 text-[#666]" strokeWidth={2} />
              <input
                type="text"
                required
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="FP-XXXXXX..."
                className="flex-1 px-3 py-2.5 font-mono text-base tracking-wide focus:outline-none uppercase"
                data-testid="lead-code-input"
                autoFocus
                autoComplete="off"
              />
            </div>
            <div className="mt-6 flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={() => setMode("lead")}
                disabled={keyChecking}
                className="border-2 border-black bg-white px-4 py-2.5 font-mono font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#fff8d6] disabled:opacity-50"
                data-testid="lead-code-cancel"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={keyChecking || !keyInput.trim()}
                className="border-2 border-black bg-[#0a0a0a] text-[#FFD700] px-5 py-2.5 font-mono font-bold uppercase tracking-[0.18em] text-xs inline-flex items-center gap-2 hover:bg-[#FFD700] hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="lead-code-submit"
              >
                {keyChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                {keyChecking ? "Verifikasi..." : "Aktifkan"}
              </button>
            </div>
          </form>
        )}

        {/* LEAD CAPTURE VIEW (default) */}
        {!submitted && mode === "lead" && (
          <form onSubmit={handleSubmit} className="p-6 md:p-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#666]">◆ Notifikasi Premium</div>
            <h3 className="mt-2 font-display font-black uppercase tracking-tighter text-2xl md:text-3xl leading-[0.95]">
              Minat dengan Full Audit?
            </h3>
            <p className="mt-3 text-sm font-sans leading-relaxed text-[#333]">
              Tinggalkan email Anda. Kami akan menghubungi Anda saat akses Premium tersedia.
            </p>

            {product && (
              <div className="mt-4 border-2 border-black bg-[#fff8d6] p-3 font-mono text-[10px] uppercase tracking-[0.18em]">
                <div className="text-[#666]">Produk konteks</div>
                <div className="mt-1 font-bold text-[#0a0a0a] truncate" title={product}>
                  {product}
                </div>
              </div>
            )}

            <label className="mt-5 block font-mono text-[10px] uppercase tracking-[0.22em] text-[#666]">
              Email Address
            </label>
            <div className="mt-1 flex items-center border-2 border-black bg-white">
              <Mail className="w-4 h-4 ml-3 text-[#666]" strokeWidth={2} />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@email.com"
                className="flex-1 px-3 py-2.5 font-sans text-base focus:outline-none"
                data-testid="lead-email-input"
                autoFocus
              />
            </div>

            <div className="mt-6 flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className="border-2 border-black bg-white px-4 py-2.5 font-mono font-bold uppercase tracking-[0.18em] text-xs hover:bg-[#fff8d6] disabled:opacity-50"
                data-testid="lead-cancel-button"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="border-2 border-black bg-[#0a0a0a] text-[#FFD700] px-5 py-2.5 font-mono font-bold uppercase tracking-[0.18em] text-xs inline-flex items-center gap-2 hover:bg-[#FFD700] hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="lead-submit-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {loading ? "Mengirim..." : "Kirim"}
              </button>
            </div>

            {/* Hidden admin override link */}
            <div className="mt-5 pt-4 border-t border-dashed border-[#ccc] text-center">
              <button
                type="button"
                onClick={() => setMode("code")}
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#999] hover:text-[#0a0a0a] inline-flex items-center gap-1.5"
                data-testid="lead-admin-link"
              >
                <Key className="w-3 h-3" /> Punya kode akses?
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
